// Commission/hooks.ts
import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import { commissionApi } from "./api";

import GetCommissionResponse = App.Http.Responses.Commission.GetCommissionResponse;
import StoreCommissionRequest = App.Http.Requests.Commission.StoreCommissionRequest;
import UpdateCommissionRequest = App.Http.Requests.Commission.UpdateCommissionRequest;
import GetCommissionsRequest = App.Http.Requests.Commission.GetCommissionsRequest;
import GetCommissionsResponse = App.Http.Responses.Commission.GetCommissionsResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;

// Query to get a list of commissions
export const useGetCommissionsQuery = (key: string, request: GetCommissionsRequest, options = {}) => {
    return useQueryApi<GetCommissionsResponse>({
        queryKey: ["getCommissions", key],
        queryFn: () => commissionApi.index(request),
        ...options,
        refetchOnMount: "always",
        refetchOnWindowFocus: false,
    });
};

// Query to get a specific commission
export const useGetCommissionQuery = (commissionId: string, request?: unknown, options = {}) => {
    return useQueryApi<GetCommissionResponse>({
        queryKey: ["getCommission", commissionId],
        queryFn: () => commissionApi.show(commissionId, request),
        ...options,
    });
};

// Mutation to store a new commission
export const useStoreCommissionMutation = (
    options: UseMutationOptions<GetCommissionResponse, AxiosError<ApiErrorResponse>, StoreCommissionRequest> = {}
) => {
    return useMutationApi<GetCommissionResponse, StoreCommissionRequest>({
        mutationKey: ["storeCommission"],
        mutationFn: (request) => commissionApi.store(request),
        ...options,
    });
};

// Mutation to update an existing commission
export const useUpdateCommissionMutation = (
    options: UseMutationOptions<
        GetCommissionResponse,
        AxiosError<ApiErrorResponse>,
        { commissionId: string; request: UpdateCommissionRequest }
    > = {}
) => {
    return useMutationApi<GetCommissionResponse, { commissionId: string; request: UpdateCommissionRequest }>({
        mutationKey: ["updateCommission"],
        mutationFn: ({ commissionId, request }) => commissionApi.update(commissionId, request),
        ...options,
    });
};

// Mutation to destroy a commission
export const useDestroyCommissionMutation = (
    options: UseMutationOptions<null, AxiosError<ApiErrorResponse>, string> = {}
) => {
    return useMutationApi<null, string>({
        mutationKey: ["destroyCommission"],
        mutationFn: (cascadeId: string) => commissionApi.destroy(cascadeId),
        ...options,
    });
};

export const useExportCommissionsMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportCommissions"],
        mutationFn: (request) => commissionApi.export(request),
        ...options,
    });
};
